/**
 * What each report type is, what it needs to be given, and what it prints.
 *
 * One registry so the editor, the preview and the run path cannot disagree
 * about a report. Adding a report type here gives it a place in the library, a
 * set of controls, a live preview outline and a render — there is nowhere else
 * to remember to update.
 *
 * `engineTemplate` mirrors TEMPLATE_FOR_TYPE in
 * `backend/app/api/v1/report_definitions.py`. It is derived, never chosen by
 * the user, so a definition can never be pointed at a builder that does not
 * understand its configuration.
 */
import {
  BarChart3,
  ClipboardCheck,
  FileText,
  Gauge,
  History,
  ListOrdered,
} from 'lucide-react'
import type { ReportTypeId } from '@/services/reportDefinitions'

export type ScopeKind = 'deal' | 'committee' | 'period' | 'portfolio'

export type ReportFieldKind =
  | 'deal'
  | 'text'
  | 'date'
  | 'select'
  | 'number'
  | 'multiselect'
  /** The dossier's section catalogue, fetched per deal so availability is real. */
  | 'dossierSections'

export type ReportField = {
  key: string
  label: string
  kind: ReportFieldKind
  help?: string
  options?: Array<{ value: string; label: string; description?: string }>
  /** Without this the report cannot render, and Run/Export stay disabled. */
  required?: boolean
  min?: number
  max?: number
  placeholder?: string
}

export type ReportTypeSpec = {
  id: ReportTypeId
  name: string
  code: string
  icon: typeof FileText
  engineTemplate: string
  scope: ScopeKind
  description: string
  /** The numbered sections the produced document carries, in printing order.
   *  Used for the live outline; the builder owns the real order. */
  sections: string[]
  fields: ReportField[]
  formats: Array<'pdf' | 'xlsx'>
}

/**
 * The Pipeline Changes sections, in the order the document prints them. The
 * values are the server's section keys (`SECTIONS` in
 * `backend/app/reporting/pipeline_changes.py`); the headings are what it prints.
 */
export const CHANGE_SECTIONS: Array<{ value: string; label: string; heading: string; description: string }> = [
  {
    value: 'new_deals',
    label: 'New deals',
    heading: 'New Deals',
    description: 'Deals opened in the window, dated by when the package was first received.',
  },
  {
    value: 'status_moves',
    label: 'Status moves',
    heading: 'Status Moves',
    description: 'Every move from one status to another, with who and when. Backward moves say so.',
  },
  {
    value: 'kills',
    label: 'Kills (with reasons)',
    heading: 'Kills',
    description: 'Deals killed in the window: the status they died at, the reason, the note and who.',
  },
  { value: 'revived', label: 'Revived', heading: 'Revived', description: 'Dead deals brought back.' },
  { value: 'funded', label: 'Funded', heading: 'Funded', description: 'Deals that reached Funded.' },
  {
    value: 'other_edits',
    label: 'Other edits',
    heading: 'Other Edits',
    description: 'Fields a person changed, grouped by deal: what it was, what it is now, who and when.',
  },
]

/** With no dates, how many days back the report reads (the server's DEFAULT_DAYS). */
export const CHANGES_DEFAULT_DAYS = 7

const SCREENER_VARIATIONS = [
  {
    value: 'sizing_memo',
    label: 'Sizing memo',
    description: 'One deal: what was screened, the rules tested, the layers, the ruleset.',
  },
  {
    value: 'credit_box',
    label: 'Credit box screen',
    description: 'One deal, rules first: every check, its outcome and the detail behind it.',
  },
  {
    value: 'structure',
    label: 'Suggested structure',
    description: 'One deal: cumulative attachment by layer — amount, LTV, debt yield.',
  },
  {
    value: 'queue_log',
    label: 'Screening queue log',
    description: 'A period: everything screened, its outcome, and its disposition.',
  },
]

export const REPORT_TYPES: ReportTypeSpec[] = [
  {
    id: 'pipeline',
    name: 'Pipeline Report',
    code: 'PIP',
    icon: ListOrdered,
    engineTemplate: 'pipeline_summary',
    scope: 'portfolio',
    description:
      'The deal book as it stands — columns, filters, grouping, totals and styling you choose, printed exactly as previewed.',
    sections: ['Pipeline table'],
    // Pipeline is designed in Report Studio, which owns its own controls.
    fields: [],
    formats: ['pdf', 'xlsx'],
  },
  {
    id: 'committee_book',
    name: 'Committee Book',
    code: 'CMB',
    icon: ClipboardCheck,
    engineTemplate: 'committee_book',
    scope: 'committee',
    description:
      'The papers for one committee sitting: an agenda, each deal’s memorandum as its own section, then the ballot and the conditions carried out of the room.',
    sections: ['Agenda', 'One section per tabled memo', 'Ballot and Decisions', 'Statement of Record'],
    fields: [
      {
        key: 'committee_date',
        label: 'Committee date',
        kind: 'date',
        help: 'The sitting this book is for. Leave empty to print every open memo.',
      },
      {
        key: 'statuses',
        label: 'Memo statuses',
        kind: 'multiselect',
        help: 'Which memos are tabled. Empty means every status.',
        options: [
          { value: 'Draft', label: 'Draft' },
          { value: 'In Review', label: 'In Review' },
          { value: 'Approved', label: 'Approved' },
          { value: 'Declined', label: 'Declined' },
        ],
      },
    ],
    formats: ['pdf'],
  },
  {
    id: 'screener',
    name: 'Screener',
    code: 'SCR',
    icon: Gauge,
    engineTemplate: 'screener',
    scope: 'deal',
    description:
      'What the credit box said, printed. The screen is not recomputed: this reproduces the recorded result against the ruleset it was tested with.',
    sections: ['Inputs Screened', 'Rules Tested', 'Attachment by Layer', 'Ruleset and Referrals'],
    fields: [
      {
        key: 'variation',
        label: 'Variation',
        kind: 'select',
        required: true,
        options: SCREENER_VARIATIONS,
      },
      {
        key: 'loan_id',
        label: 'Deal',
        kind: 'deal',
        help: 'Required for every variation except the queue log.',
      },
      {
        key: 'days',
        label: 'Window (days)',
        kind: 'number',
        min: 1,
        max: 365,
        help: 'Queue log only — how far back the log reaches.',
      },
    ],
    formats: ['pdf'],
  },
  {
    id: 'dossier',
    name: 'Deal Dossier',
    code: 'DOS',
    icon: FileText,
    engineTemplate: 'deal_package',
    scope: 'deal',
    description:
      'One deal’s file printed whole: the sections you choose, reproduced from the record with nothing supplied where it is silent.',
    sections: ['Chosen sections, in the dossier’s own order', 'Statement of Record'],
    fields: [
      { key: 'loan_id', label: 'Deal', kind: 'deal', required: true },
      {
        key: 'document_kind',
        label: 'Document',
        kind: 'select',
        options: [
          { value: 'book', label: 'Deal Book', description: 'The institutional book with cover and contents.' },
          { value: 'package', label: 'Deal Package', description: 'The older package chrome.' },
        ],
      },
      {
        key: 'sections',
        label: 'Sections',
        kind: 'dossierSections',
        help: 'Empty means every section. A section the deal has no content for still prints, stating so.',
      },
    ],
    formats: ['pdf'],
  },
  {
    id: 'asset_management',
    name: 'Asset Management Report',
    code: 'AMR',
    icon: BarChart3,
    engineTemplate: 'asset_management',
    scope: 'period',
    description:
      'The book after the deal funds: posture by tier, covenant tests, the maturity ladder, borrower requests awaiting decision, and reporting that is late.',
    sections: [
      'Portfolio Posture',
      'Covenant Compliance',
      'Maturity Profile',
      'Borrower Requests',
      'Reporting and Action Plans',
    ],
    fields: [
      {
        key: 'days',
        label: 'Lookback (days)',
        kind: 'number',
        min: 1,
        max: 730,
        help: 'How far back covenant tests and decided requests are drawn from.',
      },
      {
        key: 'tiers',
        label: 'Monitoring tiers',
        kind: 'multiselect',
        help: 'Empty means every tier.',
        options: [
          { value: 'onboarding', label: 'Onboarding' },
          { value: 'routine', label: 'Routine' },
          { value: 'heightened', label: 'Heightened' },
          { value: 'watchlist', label: 'Watchlist' },
          { value: 'modification', label: 'Modification' },
          { value: 'workout', label: 'Workout' },
        ],
      },
    ],
    formats: ['pdf'],
  },
  {
    id: 'pipeline_changes',
    name: 'Pipeline Changes',
    code: 'CHG',
    icon: History,
    engineTemplate: 'pipeline_changes',
    scope: 'period',
    description:
      'What changed on the deal book between two dates: deals opened, status moves, kills and their reasons, revivals, fundings and the edits people made — each in its own section.',
    sections: ['Summary', ...CHANGE_SECTIONS.map((section) => section.heading)],
    fields: [
      {
        key: 'from_date',
        label: 'From',
        kind: 'date',
        help: 'The first day, from midnight New York time. Leave From and To empty to read the last N days instead — what a schedule should do.',
      },
      {
        key: 'to_date',
        label: 'To',
        kind: 'date',
        help: 'The last day, included in full. Empty with a From date means through today.',
      },
      {
        key: 'days',
        label: 'Last N days',
        kind: 'number',
        min: 1,
        max: 366,
        help: 'Used when no From date is chosen: the N days before today (or before the To date), through it.',
      },
      {
        key: 'sections',
        label: 'Sections',
        kind: 'multiselect',
        help: 'Empty means every section. The summary always prints first.',
        options: CHANGE_SECTIONS.map(({ value, label, description }) => ({ value, label, description })),
      },
    ],
    formats: ['pdf', 'xlsx'],
  },
]

export const REPORT_TYPE_MAP = new Map(REPORT_TYPES.map((spec) => [spec.id, spec]))

export function reportTypeSpec(id: string): ReportTypeSpec {
  return REPORT_TYPE_MAP.get(id as ReportTypeId) ?? REPORT_TYPES[0]
}

/**
 * The sections THIS configuration prints.
 *
 * The screener's queue log is a different document from its three single-deal
 * variations — a period record rather than one deal's screen — and lists a
 * different set of sections. Reading the list off the report type alone would
 * promise sections the document does not carry.
 */
export function sectionsFor(
  spec: ReportTypeSpec,
  filters: Record<string, unknown>,
): string[] {
  if (spec.id === 'screener' && String(filters.variation ?? '') === 'queue_log') {
    return ['Period Summary', 'Screening Log']
  }
  if (spec.id === 'screener' && String(filters.variation ?? '') === 'credit_box') {
    // The credit-box screen leads with the rules; the builder orders it so.
    return ['Rules Tested', 'Inputs Screened', 'Ruleset and Referrals']
  }
  if (spec.id === 'screener' && String(filters.variation ?? '') === 'structure') {
    return ['Attachment by Layer', 'Inputs Screened', 'Ruleset and Referrals']
  }
  if (spec.id === 'pipeline_changes') {
    // The chosen sections in the document's own order; none chosen is all.
    const chosen = Array.isArray(filters.sections) ? (filters.sections as string[]) : []
    const picked = CHANGE_SECTIONS.filter((section) => chosen.includes(section.value))
    return ['Summary', ...(picked.length ? picked : CHANGE_SECTIONS).map((section) => section.heading)]
  }
  return spec.sections
}

/** A `YYYY-MM-DD` control value as the document prints it (MM/DD/YYYY), read
 *  as a calendar date — never through `Date`, which would shift it a day west
 *  of UTC. */
function printedDay(value: unknown): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value ?? '').trim())
  return match ? `${match[2]}/${match[3]}/${match[1]}` : null
}

/** The changes window as the cover states it. */
function changesWindow(filters: Record<string, unknown>): string {
  const from = printedDay(filters.from_date)
  const to = printedDay(filters.to_date)
  const days = Number(filters.days ?? CHANGES_DEFAULT_DAYS) || CHANGES_DEFAULT_DAYS
  if (from && to) return `${from} – ${to}`
  if (from) return `${from} – today`
  if (to) return `The ${days} days up to ${to}`
  return `Last ${days} days`
}

/**
 * What the document is about, in one line — the cover's subject.
 *
 * Derived from the configuration rather than the report type, because a
 * screener's queue log is about a period and its sizing memo is about a deal.
 */
export function subjectFor(
  spec: ReportTypeSpec,
  filters: Record<string, unknown>,
  dealName: string | null,
): string {
  if (spec.id === 'screener' && String(filters.variation ?? '') === 'queue_log') {
    return `Last ${String(filters.days ?? 30)} days`
  }
  if (spec.id === 'pipeline_changes') return changesWindow(filters)
  if (spec.scope === 'deal') return dealName || 'No deal chosen'
  if (spec.scope === 'committee') return String(filters.committee_date || 'Open items')
  if (spec.scope === 'period') return `Lookback ${String(filters.days ?? 90)} days`
  return 'Portfolio'
}

/** Report-type defaults for a brand-new definition. */
export function initialFilters(id: ReportTypeId): Record<string, unknown> {
  if (id === 'screener') return { variation: 'sizing_memo' }
  if (id === 'asset_management') return { days: 90 }
  if (id === 'pipeline_changes') return { days: CHANGES_DEFAULT_DAYS }
  if (id === 'dossier') return { document_kind: 'book' }
  return {}
}

/**
 * Whether a configuration can actually be rendered, and what is missing.
 *
 * Returned as a reason rather than a boolean so the editor can say what to
 * supply. A disabled button with no explanation is the thing this module
 * exists to stop.
 */
export function missingRequirement(
  spec: ReportTypeSpec,
  filters: Record<string, unknown>,
): string | null {
  if (spec.id === 'screener') {
    const variation = String(filters.variation ?? 'sizing_memo')
    if (variation !== 'queue_log' && !filters.loan_id) {
      return 'Choose a deal — every screener variation except the queue log is about one deal.'
    }
    return null
  }
  if (spec.id === 'pipeline_changes') {
    // Refused, never swapped: the server says the same.
    const from = String(filters.from_date ?? '')
    const to = String(filters.to_date ?? '')
    if (from && to && from > to) {
      return 'The From date is after the To date. Choose a From date on or before the To date.'
    }
    return null
  }
  for (const field of spec.fields) {
    if (field.required && !filters[field.key]) {
      return `${field.label} is required before this report can be produced.`
    }
  }
  return null
}

/** Which fields matter for the chosen configuration. */
export function visibleFields(
  spec: ReportTypeSpec,
  filters: Record<string, unknown>,
): ReportField[] {
  if (spec.id === 'pipeline_changes') {
    // With a From date the day count reads nothing, so it is not offered.
    return spec.fields.filter((field) => field.key !== 'days' || !filters.from_date)
  }
  if (spec.id !== 'screener') return spec.fields
  const variation = String(filters.variation ?? 'sizing_memo')
  return spec.fields.filter((field) => {
    if (field.key === 'loan_id') return variation !== 'queue_log'
    if (field.key === 'days') return variation === 'queue_log'
    return true
  })
}
